import { createContext, useContext } from 'react'
import type { ApiFailure } from '@/api/client'
import type { OnboardingState } from './steps'

export interface OnboardingValue {
  status: 'loading' | 'ready' | 'error'
  failure: ApiFailure | null
  /** Null only while loading or after a failure with nothing cached. */
  state: OnboardingState | null
  /**
   * Adopt the state a mutation just returned.
   *
   * Every write route answers with the whole state (`app/v1/onboarding.py`), so
   * a step that has just saved knows the new cursor and the new progress without
   * a second request. Refetching instead would leave one render where the
   * progress bar and the step disagreed — and on a wizard that renders the two
   * next to each other, that is visible.
   */
  adopt: (next: OnboardingState) => void
  reload: () => void
}

export const OnboardingContext = createContext<OnboardingValue | null>(null)

export function useOnboarding(): OnboardingValue {
  const value = useContext(OnboardingContext)
  if (!value) throw new Error('useOnboarding was called outside <OnboardingProvider>')
  return value
}

/**
 * The wizard state, for the screens the layout has already vetted.
 *
 * Throws rather than returning null for the same reason as `useActiveTenant`:
 * `WizardLayout` renders nothing but a loader until the state is there, so a
 * step component that saw null would be a wiring bug, and a wizard rendering
 * with an empty progress bar is exactly the kind of bug that gets shipped.
 */
export function useWizard(): OnboardingState {
  const { state } = useOnboarding()
  if (!state) throw new Error('useWizard was called outside a loaded wizard')
  return state
}
